import classNames from "classnames";
import {differenceInSeconds} from "date-fns";
import {useRef} from "react";
import {GrHistory} from "react-icons/gr";
import {useLocation, useNavigate} from "react-router";

import {Button} from "@/components/button/Button.tsx";
import {Card} from "@/components/card/Card.tsx";
import {CircularProgressBar} from "@/components/circularProgressBar/CircularProgressBar.tsx";
import {useCurrentUserContext} from "@/components/context/hooks.ts";
import {FreeTrialInstantCheckout} from "@/components/freeTrialExpired/FreeTrialInstantCheckout.tsx";
import {Icon} from "@/components/icon/Icon.tsx";
import {REQUEST_DEMO} from "@/lib/featureFlags.ts";
import {URL_ORIGINAL_WEBSITE} from "@/lib/links.ts";
import {AccountSubscriptionType} from "@/lib/types/accounts.ts";
import {LimitCheckerComponent} from "@/pages/billing/v2/InstantCheckout/LimitCheckWithInstantCheckout.tsx";

import styles from "./FreeTrialExpired.module.scss";

const EXCLUDED_PATH_PREFIXES = [
  "/billing",
  "/onboarding",
  "/checkout",
  "/not-found",
  "/access-denied",
  "/feature-unavailable",
  "/contact",
];
const PATH_PREFIXES = ["/dashboard"];
const EXACT_PATHS = ["/checks", "/alerts", "/statuspages/all", "/statuspages/incidents"];

const getShowOnLocation = (freeTrialRemaining: number, pathname: string) => {
  if (EXCLUDED_PATH_PREFIXES.some((path) => pathname.startsWith(path))) {
    return false;
  } else if (freeTrialRemaining > 0) {
    return EXACT_PATHS.includes(pathname) || PATH_PREFIXES.some((path) => pathname.startsWith(path));
  } else {
    return true;
  }
};

const getCardVariant = (freeTrialRemaining: number) => {
  if (freeTrialRemaining > 6) {
    return "blue";
  } else if (freeTrialRemaining > 1) {
    return "red600";
  } else if (freeTrialRemaining === 1) {
    return "red500";
  } else {
    return "error";
  }
};

export const FreeTrialExpired = () => {
  const instantCheckoutRef = useRef<LimitCheckerComponent>(null);
  const location = useLocation();
  const navigate = useNavigate();

  const {user, userHasFeature} = useCurrentUserContext();
  const freeTrialExpiresAt = user?.account?.free_trial_expires_at;
  if (!freeTrialExpiresAt) {
    return null;
  }

  const freeTrialRemainingPrecise = differenceInSeconds(freeTrialExpiresAt, new Date()) / (24 * 60 * 60);
  const freeTrialRemaining =
    freeTrialRemainingPrecise < 0 ? Math.floor(freeTrialRemainingPrecise) : Math.ceil(freeTrialRemainingPrecise);

  const showOnLocation = getShowOnLocation(freeTrialRemaining, location.pathname);
  if (!showOnLocation) {
    return null;
  }

  return (
    <>
      <FreeTrialInstantCheckout ref={instantCheckoutRef} freeTrialRemaining={freeTrialRemaining} />
      <Card
        variant={getCardVariant(freeTrialRemaining)}
        className="py-4 px-3 px-sm-4 pulseShadow mb-sm-4 mb-5 mt-sm-3 mt-4"
      >
        <div className={classNames("d-flex align-items-center gap-5 gap-sm-3 flex-column flex-sm-row", styles.body)}>
          {freeTrialRemaining > 0 && (
            <div>
              <CircularProgressBar
                value={Math.min(freeTrialRemaining / 15, 1)}
                size="medium"
                startPosition="right"
                rotation="counterclockwise"
                progressColor={freeTrialRemaining > 6 ? styles.progressBlue : styles.progressRed}
                backgroundColor={styles.background}
                text={freeTrialRemaining.toString()}
                textClassName="pulseTextH3Bold"
              />
            </div>
          )}
          <div className={styles.text}>
            <div className="pulseTextH3Bold mb-2">
              {freeTrialRemaining > 0 ? (
                <>
                  <span className="pulse-color-gray-900">Your Free Trial Expires in </span>
                  <span className={freeTrialRemaining > 6 ? "pulse-color-blue-500" : "pulse-color-red-600"}>
                    {freeTrialRemaining} day{freeTrialRemaining !== 1 && "s"}
                  </span>
                </>
              ) : (
                <span className="pulse-color-red-800">
                  <Icon icon={GrHistory} display="inline" />
                  Your Free Trial Has Expired
                </span>
              )}
            </div>
            <div className="pulseTextBody">
              {freeTrialRemaining > 0 ? (
                <span className="pulse-color-gray-700">
                  Subscribe now to keep monitoring your sites without interruption.
                </span>
              ) : (
                <span className="pulse-color-red-800">
                  All monitoring activities are currently paused. Subscribe now to resume monitoring and alerts without
                  interruption.
                </span>
              )}
            </div>
          </div>
          <div className={classNames("d-flex gap-3 gap-sm-2 text-nowrap flex-column flex-md-row", styles.buttons)}>
            {userHasFeature(REQUEST_DEMO) && (
              <Button as={"a"} href={`${URL_ORIGINAL_WEBSITE}/request-demo`} variant="secondary">
                Request Demo
              </Button>
            )}
            <Button
              variant={freeTrialRemaining > 6 ? "blue" : "red"}
              onClick={() => {
                if (user?.account.subscription_type !== AccountSubscriptionType.BILLING_2_0) {
                  navigate("/billing?action=subscribe");
                } else {
                  instantCheckoutRef.current?.checkIfLimitHit();
                }
              }}
            >
              Subscribe Now
            </Button>
          </div>
        </div>
      </Card>
    </>
  );
};
